import { useMutation, useQuery } from "@tanstack/react-query";
import queryString from "query-string";

import { api } from "@services/index";
import {
  ApiErrorModel,
  QueriesResponseModelDto,
  QueriesResponseModel,
  QueryListItemModelDto,
  QueryListItemModel,
  QueryModelDto,
  QueryModel,
  QueryScopeType,
  QueryStatusType,
  FileModel,
} from "@interfaces/index";
import {
  QUERY_SCOPE_LABELS,
  QUERY_STATUS_LABELS,
  QUERY_TYPE_LABELS,
} from "@utils/index";

const prepareQueryToModel = (query: QueryModelDto): QueryModel => ({
  uniqueId: query.unique_id,
  topicId: query.topic_id,
  type: query.type,
  status: query.status,
  subject: query.subject,
  callRequested: query.call_requested,
  createdAt: query.created_at,
  messages: !query.messages?.length
    ? []
    : query.messages.map((message) => ({
        ...message,
        sentAt: message.sent_at,
        links: message.links || [],
        attachments: !message.attachments?.length
          ? []
          : message.attachments.map((attachment) => ({
              uniqueId: attachment.unique_id,
              originalName: attachment.original_name,
              url: attachment.url,
              size: attachment.size,
            })),
      })),
});

const prepareQueryListItemToModel = (
  query: QueryListItemModelDto,
): QueryListItemModel => ({
  uniqueId: query.id,
  subject: query.subject,
  type: { label: QUERY_TYPE_LABELS[query.type], value: query.type },
  status: { label: QUERY_STATUS_LABELS[query.status], value: query.status },
  scope: query.scope,
  callRequested: query.call_requested,
  messagesCount: query.messages_count,
  topicId: query.topic_id,
  business: query.business || undefined,
  lastMessage: !query.last_message
    ? undefined
    : {
        body: query.last_message.body,
        author: query.last_message.author.name,
        sender: query.last_message.sender_type,
        sentAt: query.last_message.sent_at,
      },
  createdAt: query.created_at,
  updatedAt: query.updated_at,
});

const QUERY_TAB_VALUES: QueryScopeType[] = ["submission", "tax_return"];

const QUERY_SUBTAB_VALUES: QueryStatusType[] = [
  "awaiting_client",
  "awaiting_fbtc",
  "resolved",
];

export const useQueryGetQueries = ({
  businessId = "",
  startDate = "",
  endDate = "",
  scope = "",
  status = "",
}: {
  businessId?: string;
  startDate?: string;
  endDate?: string;
  scope?: QueryScopeType | "";
  status?: QueryStatusType | "";
}) => {
  const queryFn = async (): Promise<QueriesResponseModel> => {
    const params = queryString.stringify(
      {
        scope,
        status,
        user_business_unique_id: businessId,
        start_date: startDate ? `${startDate}T00:00:00` : "",
        end_date: endDate ? `${endDate}T23:59:59` : "",
      },
      { skipEmptyString: true },
    );
    const { data: dataDto } = await api.get<QueriesResponseModelDto>(
      `/queries?${params}`,
    );
    const { by_scope, by_business } = dataDto.meta.summary.awaiting_client;
    const businessCounts: Record<string, number> = Object.fromEntries(
      by_business.map(({ business, count }) => [business.id, count]),
    );
    // Same number as the selected business (submission) or the tab (tax
    // return), so it doesn't change when switching subtabs
    let actionRequiredCount: number | undefined;
    if (scope === "submission") {
      actionRequiredCount = businessCounts[businessId] || 0;
    } else if (scope === "tax_return") {
      actionRequiredCount = by_scope.tax_return;
    }

    return {
      tabs: QUERY_TAB_VALUES.map((value) => ({
        value,
        label: QUERY_SCOPE_LABELS[value],
        count: by_scope[value],
      })),
      // Only "Action Required" shows a count
      subtabs: QUERY_SUBTAB_VALUES.map((value) => ({
        value,
        label: QUERY_STATUS_LABELS[value],
        count: value === "awaiting_client" ? actionRequiredCount : undefined,
      })),
      queries: dataDto.data.map(prepareQueryListItemToModel),
      businessCounts,
    };
  };

  return useQuery<QueriesResponseModel, ApiErrorModel>({
    queryKey: [
      "queryGetQueries",
      businessId,
      startDate,
      endDate,
      scope,
      status,
    ],
    queryFn,
    refetchOnWindowFocus: true,
  });
};

export const useQueryGetOne = (id: string) => {
  const queryFn = async () => {
    const { data: dataDto } = await api.get<QueryModelDto>(`/queries/${id}`);

    return prepareQueryToModel(dataDto);
  };

  return useQuery<QueryModel, ApiErrorModel>({
    queryKey: ["queryGetOne", id],
    queryFn,
    enabled: !!id,
  });
};

export const useQueryReply = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    queryId,
    message,
    files,
  }: {
    queryId: string;
    message: string;
    files?: FileModel[];
  }) => {
    const fieldsDto = {
      message,
      files: files?.map((file) => ({
        key: file.key,
        uuid: file.uuid,
        original_name: file.originalName,
      })),
    };
    await api.post(`/queries/${queryId}/messages`, fieldsDto);
  };

  return useMutation({
    mutationKey: ["queryReply"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        message: error.fields?.message || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};
